/**
 * DEC-1148 (FEEDBACK-4): draft persistence + the human-readable page label.
 *
 * Both are pure and live outside the components so they're testable without
 * rendering, and so the "never show an internal id to a user" rule has one
 * enforcement point rather than being re-decided per surface.
 */

const DRAFT_KEY = 'deco_feedback_draft';
// A draft older than this is more likely to confuse than help — restoring
// last week's half-written complaint into a fresh panel reads as a bug.
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Route → the words a person would use for that screen.
 *
 * NEVER interpolate an id. "Site 3f2504e0-…" is meaningless to the user, and
 * the no-internal-ids-in-UI standard applies to this line like any other.
 * Unknown routes fall back to a generic phrase rather than leaking the path.
 */
const ROUTE_LABELS = [
  [/^\/$/, 'your dashboard'],
  [/^\/dashboard/, 'your dashboard'],
  [/^\/sites\/[^/]+\/setup/, 'site setup'],
  [/^\/sites\/[^/]+/, 'a site’s details'],
  [/^\/sites/, 'your sites'],
  [/^\/programs\/[^/]+/, 'a program’s page'],
  [/^\/programs/, 'programs'],
  [/^\/enroll/, 'program enrollment'],
  [/^\/profile/, 'your profile'],
  [/^\/organizations/, 'organization settings'],
  [/^\/admin/, 'an admin page'],
  [/^\/getting-started/, 'getting started'],
];

export function pageLabelFor(pathname) {
  if (!pathname || typeof pathname !== 'string') return 'this page';
  const hit = ROUTE_LABELS.find(([re]) => re.test(pathname));
  return hit ? hit[1] : 'this page';
}

/** Path only — the stored route must never carry a query string (DEC-1147). */
export function currentPath() {
  if (typeof window === 'undefined') return '';
  return window.location.pathname || '';
}

export function saveDraft(fields) {
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify({ fields, ts: Date.now() }));
  } catch {
    // Private browsing / quota. Losing a draft is not worth an error state.
  }
}

/** Returns the saved fields, or null if absent, stale, or unparseable. */
export function loadDraft() {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fields || !parsed?.ts) return null;
    if (Date.now() - parsed.ts > DRAFT_MAX_AGE_MS) {
      clearDraft();
      return null;
    }
    return parsed.fields;
  } catch {
    return null;
  }
}

export function clearDraft() {
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch { /* no-op */ }
}

/** A draft worth restoring has actual prose in it, not just a chip selection. */
export function isRestorable(fields) {
  return !!(fields && typeof fields.body === 'string' && fields.body.trim().length > 0);
}

export const DRAFT_LIMITS = { DRAFT_KEY, DRAFT_MAX_AGE_MS };
